import { getBaseRole } from '@/helpers/customRoles';
import type {
  IntegrationItem,
  IntegrationProvider,
} from '@/api/integrations/integrations.types'

export type NotificationIntegrationStatus =
  | 'hidden'
  | 'setup'
  | 'activate'
  | 'connected'

export const getNotificationIntegrationRefetchEvent = (
  provider: IntegrationProvider,
) => `${provider}-notification-integration:refetch`

/** @deprecated use getNotificationIntegrationRefetchEvent('telegram') */
export const TELEGRAM_NOTIFICATION_BOT_REFETCH_EVENT =
  getNotificationIntegrationRefetchEvent('telegram')

export const dispatchNotificationIntegrationRefetch = (
  provider: IntegrationProvider,
) => {
  window.dispatchEvent(new Event(getNotificationIntegrationRefetchEvent(provider)))
}

const NOTIFICATION_USAGE = 'notifications' as const

const hasNotificationUsage = (item: IntegrationItem): boolean => {
  const usages = item?.usages
  return Array.isArray(usages) && usages.includes(NOTIFICATION_USAGE)
}

const pickPreferredIntegration = (
  items: IntegrationItem[],
): IntegrationItem | null => {
  if (items.length === 0) {
    return null
  }

  return items.find((item) => item.status === 'connected') ?? items[0]
}

/** Интеграция для уведомлений: только с usage `notifications`. */
export const getNotificationIntegration = (
  items: IntegrationItem[] | null | undefined,
): IntegrationItem | null => {
  if (!Array.isArray(items)) {
    return null
  }

  const list = items.filter(Boolean)
  if (list.length === 0) {
    return null
  }

  // Без fallback на CRM / password_reset / support / пустые usages.
  return pickPreferredIntegration(list.filter(hasNotificationUsage))
}

/** @deprecated use getNotificationIntegration */
export const getConnectedIntegration = getNotificationIntegration

/** Публичная ссылка на VK-сообщество: https://vk.com/club{id} */
export const buildVkCommunityUrl = (
  groupId: string | number | null | undefined,
): string | null => {
  if (groupId == null || groupId === '') return null
  const digits = String(groupId).trim().replace(/^-/, '')
  if (!/^\d+$/.test(digits)) return null
  return `https://vk.com/club${digits}`
}

/** Публичная ссылка на MAX-бота: https://max.ru/{bot_id} */
export const buildMaxBotUrl = (
  botId: string | null | undefined,
): string | null => {
  if (!botId || !String(botId).trim()) return null
  const normalized = String(botId).trim().replace(/^@/, '')
  return `https://max.ru/${normalized}`
}

export const getIntegrationPublicUrl = (
  integration: IntegrationItem | null,
): string | null => {
  if (!integration) return null

  if (integration.provider === 'telegram') {
    const username = (integration.settings as any)?.bot_username
    return username ? `https://t.me/${String(username).replace(/^@/, '')}` : null
  }

  if (integration.provider === 'vkontakte') {
    const groupId =
      (integration.settings as any)?.group_id ?? integration.credentials?.group_id
    return buildVkCommunityUrl(groupId)
  }

  if (integration.provider === 'max') {
    return buildMaxBotUrl(integration.credentials?.bot_id)
  }

  return null
}

export const isActivationRequiredForRole = (
  role: string | null | undefined,
  integration: IntegrationItem | null,
): boolean => {
  const required = (integration?.settings as any)?.activation
  if (!required) return false

  switch (getBaseRole(role)) {
    case 'children':
      return required.required_for_students === true
    case 'parent':
      return required.required_for_parents === true
    default:
      return required.required_for_employees === true
  }
}

export const getIntegrationDisplayHandle = (
  integration: IntegrationItem | null,
): string | null => {
  if (!integration) return null

  if (integration.provider === 'telegram') {
    return (integration.settings as any)?.bot_username || null
  }

  if (integration.provider === 'vkontakte') {
    const groupId =
      (integration.settings as any)?.group_id ?? integration.credentials?.group_id
    return groupId != null && String(groupId) !== '' ? String(groupId) : null
  }

  if (integration.provider === 'max') {
    const botId = integration.credentials?.bot_id
    return botId ? String(botId) : null
  }

  return null
}

export const NOTIFICATION_INTEGRATION_UI = {
  telegram: {
    provider: 'telegram' as const,
    shortTitle: 'ТГ-Бот для уведомлений',
    menuLabel: 'Telegram-бот для уведомлений',
    detachMenuLabel: 'Отвязать Telegram-бота для уведомлений',
    openLinkLabel: 'открыть Telegram',
    instructionWithLink:
      'Перейдите по ссылке в Telegram, нажмите «Start», затем введите полученный код.',
    successAttachText: 'Telegram-бот успешно привязан',
    detachTitle: 'Отвязать ТГ-Бот для уведомлений',
    detachConfirmPrefix: 'Вы уверены, что хотите отвязать Telegram-бот для уведомлений',
    detachAfterText: 'После отвязки уведомления в Telegram перестанут приходить.',
    detachErrorText: 'Не удалось отвязать Telegram-бота.',
    successDetachText: 'Telegram-бот успешно отвязан',
    formatHandle: (handle: string | null) => (handle ? `@${handle}` : null),
  },
  vkontakte: {
    provider: 'vkontakte' as const,
    shortTitle: 'VK-сообщество для уведомлений',
    menuLabel: 'VK-сообщество для уведомлений',
    detachMenuLabel: 'Отвязать VK-сообщество для уведомлений',
    openLinkLabel: 'открыть VK',
    instructionWithLink:
      'Перейдите по ссылке в VK, напишите сообществу слово «старт», затем введите полученный код.',
    activationCommand: 'старт',
    successAttachText: 'VK-сообщество успешно привязано',
    detachTitle: 'Отвязать VK-сообщество для уведомлений',
    detachConfirmPrefix:
      'Вы уверены, что хотите отвязать VK-сообщество для уведомлений',
    detachAfterText: 'После отвязки уведомления во ВКонтакте перестанут приходить.',
    detachErrorText: 'Не удалось отвязать VK-сообщество.',
    successDetachText: 'VK-сообщество успешно отвязано',
    formatHandle: (handle: string | null) => (handle ? `club${handle}` : null),
  },
  max: {
    provider: 'max' as const,
    shortTitle: 'MAX-бот для уведомлений',
    menuLabel: 'MAX-бот для уведомлений',
    detachMenuLabel: 'Отвязать MAX-бота для уведомлений',
    openLinkLabel: 'открыть MAX',
    instructionWithLink:
      'Перейдите по ссылке в MAX, нажмите «Начать», затем введите полученный код.',
    successAttachText: 'MAX-бот успешно привязан',
    detachTitle: 'Отвязать MAX-бот для уведомлений',
    detachConfirmPrefix: 'Вы уверены, что хотите отвязать MAX-бот для уведомлений',
    detachAfterText: 'После отвязки уведомления в MAX перестанут приходить.',
    detachErrorText: 'Не удалось отвязать MAX-бота.',
    successDetachText: 'MAX-бот успешно отвязан',
    formatHandle: (handle: string | null) => (handle ? `@${handle}` : null),
  },
} as const

export const NOTIFICATION_INTEGRATION_SETTINGS_PATH =
  '/v2/platform-settings?tab=serviceIntegrations'

export const NOTIFICATION_INTEGRATION_BLOCK_IDS = {
  telegram: 'telegram-notifications',
  vkontakte: 'vk-notifications',
  max: 'max-notifications',
} as const

export type NotificationIntegrationProvider =
  keyof typeof NOTIFICATION_INTEGRATION_BLOCK_IDS

export const getNotificationIntegrationSettingsUrl = (
  provider: NotificationIntegrationProvider,
) =>
  `${NOTIFICATION_INTEGRATION_SETTINGS_PATH}#${NOTIFICATION_INTEGRATION_BLOCK_IDS[provider]}`
